/**
 * FunnelCustomization Web Component
 *
 * Manages the NMC (No More Choices) customization funnel for subscription products.
 *
 * Key Concepts:
 * - NMC Products: Free products included in the subscription (up to limit per step)
 * - NMC+ Products: Additional paid products that can be added beyond the free limit
 * - Each step has a configurable limit (e.g., "Select 2")
 *
 * Selection Rules:
 * 1. Users can select up to the limit as NMC (free) - e.g., 2 products
 * 2. Once NMC limit is reached, products can ONLY be added as NMC+ (paid)
 * 3. NMC+ products have NO LIMIT - users can add as many as they want (they pay for each)
 * 4. The SAME product can be added as NMC (once) AND as NMC+ (multiple times)
 * 5. When a user removes an NMC product, one slot opens for another free NMC selection
 *
 * Data Structure:
 * - productArray[stepIndex] = Array of selected products with nmc_type property
 * - Each product has: {variant_id, product_id, quantity, nmc_type: 'nmc' | 'nmc+', ...}
 */
class FunnelCustomization extends HTMLElement {
    constructor() {
        super();
        var _this = this;
        this.element = this;
        this.modal = this.querySelector('[data-funnel-customization]');
        this.closeBtn = this.modal?.querySelector('[data-close-popup]');
        this.siteOverlay = document.querySelector('site-overlay');
        this.body = document.body;

        // Initialize productArray
        this.productArray = [];
        this.customizedOneTimes = [];
        this.purchasedVariantSize = '';
        this.orderId = '';
        this.subscriptionType = null;
        this.nmcPlusArray = [];

        // Track completion state for Braze event tracking
        this.previousCompletionState = false;

        this.customizationDataStep = this.modal?.querySelectorAll('[customization-data-step]');
        this.customizationDataStep.forEach((step, index) => {
            _this.productArray[index] = [];
            _this.customizedOneTimes[index] = [];
        });

        this.APIHeader = {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Access-Control-Allow-Origin': '*'
        }

        // Call init after all properties are initialized
        this.init();
    }

    /**
     * Get product counts for a specific step
     * @param {number} stepIndex - The step index (0-based)
     * @returns {Object} Object containing nmcCount, nmcPlusCount, and totalCount
     */
    _getStepProductCounts(stepIndex) {
        let nmcCount = 0;
        let nmcPlusCount = 0;

        if (this.productArray[stepIndex]) {
            this.productArray[stepIndex].forEach(product => {
                if (product.nmc_type === 'nmc') {
                    nmcCount++;
                } else if (product.nmc_type === 'nmc+') {
                    nmcPlusCount++;
                }
            });
        }

        return {
            nmcCount: nmcCount,
            nmcPlusCount: nmcPlusCount,
            totalCount: nmcCount + nmcPlusCount
        };
    }

    /**
     * Check if step has reached the free product limit
     * @param {number} stepIndex - The step index (0-based)
     * @param {number} freeProductLimit - The maximum number of free products allowed
     * @returns {boolean} True if limit is reached
     */
    _hasReachedFreeProductLimit(stepIndex, freeProductLimit) {
        const counts = this._getStepProductCounts(stepIndex);
        return counts.nmcCount >= parseInt(freeProductLimit);
    }

    /**
     * Check if user can add more NMC (free) products
     * NMC+ products are UNLIMITED and not restricted by this check
     * @param {number} stepIndex - The step index (0-based)
     * @param {number} freeProductLimit - The maximum number of FREE products allowed
     * @returns {boolean} True if user can add more NMC products
     */
    _canAddMoreNMCProducts(stepIndex, freeProductLimit) {
        const counts = this._getStepProductCounts(stepIndex);
        // Can add more NMC if NMC count is less than limit
        return counts.nmcCount < parseInt(freeProductLimit);
    }

    /**
     * Log NMC selection state for debugging
     * @param {string} action - The action being performed (e.g., 'add', 'remove')
     * @param {number} stepIndex - The step index (0-based)
     * @param {number} freeProductLimit - The maximum number of products allowed
     */
    _logNMCSelectionState(action, stepIndex, freeProductLimit) {
        const counts = this._getStepProductCounts(stepIndex);
        console.log(`[NMC Selection] ${action.toUpperCase()}`, {
            step: stepIndex + 1,
            nmcProducts: counts.nmcCount,
            nmcPlusProducts: counts.nmcPlusCount,
            totalProducts: counts.totalCount,
            nmcLimit: parseInt(freeProductLimit),
            canAddMoreNMC: this._canAddMoreNMCProducts(stepIndex, freeProductLimit),
            canAddNMCPlus: true, // NMC+ is always unlimited
            productArray: this.productArray[stepIndex]
        });
    }

    /**
     * Validate free product limits and report violations to Sentry
     * @param {number} stepIndex - The step index (0-based)
     * @param {number} freeProductLimit - The maximum number of free products allowed
     * @param {string} context - Context where validation is called (e.g., 'add_product', 'submit_order')
     * @returns {Object} Validation result with isValid flag and details
     */
    _validateAndReportFreeProductLimit(stepIndex, freeProductLimit, context = 'unknown') {
        const counts = this._getStepProductCounts(stepIndex);
        const limit = parseInt(freeProductLimit) || 0;
        const nmcCount = counts?.nmcCount || 0;
        const isValid = nmcCount <= limit;
        const excessCount = isValid ? 0 : nmcCount - limit;

        // Only report to Sentry if validation fails
        if (!isValid && typeof window.Sentry !== 'undefined') {
            const currentStep = this.modal?.querySelector('[customization-data-step]:not(.d-none)');
            const collectionHandle = currentStep?.querySelector('[data-customization-collection-handle]')?.getAttribute('data-customization-collection-handle') || 'unknown';

            const errorData = {
                customization_type: 'NMC',
                step_number: stepIndex + 1,
                nmc_count: nmcCount,
                nmc_plus_count: counts?.nmcPlusCount || 0,
                free_product_limit: limit,
                excess_free_products: excessCount,
                collection_handle: collectionHandle,
                context: context,
                order_id: this.orderId || 'unknown',
                subscription_type: this.subscriptionType || 'unknown',
                product_details: this.productArray[stepIndex]?.map(p => ({
                    product_id: p.product_id,
                    variant_id: p.variant_id,
                    nmc_type: p.nmc_type,
                    quantity: p.quantity
                })) || []
            };

            window.Sentry.captureException(
                new Error(`NMC Free Product Limit Exceeded: ${excessCount} excess free product(s) in step ${stepIndex + 1}`),
                {
                    tags: {
                        error_type: 'nmc_free_product_limit',
                        validation_context: context,
                        step_number: stepIndex + 1,
                        subscription_type: this.subscriptionType || 'unknown',
                        collection: collectionHandle
                    },
                    extra: errorData,
                    level: 'error',
                    fingerprint: ['nmc-free-product-limit-exceeded', String(stepIndex + 1), context]
                }
            );

            console.error('[NMC Free Product Limit] VALIDATION FAILED:', errorData);
        }

        return { isValid, nmcCount, limit, excessCount };
    }

    init() {
        if (!this.modal) return;

        const urlParams = new URLSearchParams(window.location.search);

        if (urlParams.get('testMode') === 'true') {
            // Internal preview mode: render the NMC funnel without a real order
            // so the team can eyeball current inventory per tier. Submission
            // will fail with orderId='PREVIEW' — accepted trade-off.
            const typeMap = {
                women: 'subscription_type_women',
                men: 'subscription_type_men',
                beauty: 'subscription_type_beauty',
                home: 'subscription_type_home',
            };
            const type = (urlParams.get('type') || 'women').toLowerCase();
            this.subscriptionType = typeMap[type] || typeMap.women;
            this.orderId = 'PREVIEW';
            this.hideMessage();
            this.switchToSubscriptionCollection(this.subscriptionType);
            this.showCustomizationStep(1);
            this.renderPreviewBanner(`NMC funnel · tier: ${type}`);
        } else {
            // Get customerId and orderId from URL
            let customerId = urlParams.get('customerId');
            let orderId = urlParams.get('orderId');

            // If URL params are empty, check cookies
            if (!customerId || !orderId) {
                customerId = this.getCookie('customerId');
                orderId = this.getCookie('orderId');
            }

            // If still not found, show message
            if (!customerId || !orderId) {
                this.showMessage('direct');
            } else if(customerId && orderId) {
                // Update URL by removing query parameters
                const newUrl = window.location.pathname;
                window.history.replaceState({}, '', newUrl);

                this.orderId = orderId;
                this._setCookie('customerId', customerId, 1.5);
                this._setCookie('orderId', orderId, 1.5);
                this.hideMessage();
                this.initializeFunnel(customerId, orderId);
            }
        }

        // Close modal event
        this.closeBtn?.addEventListener('click', () => this.closeModal());

        // Close on overlay click
        this.siteOverlay?.addEventListener('click', () => this.closeModal());

        this.modal.querySelector('[data-paid-checkout]').addEventListener('click', () => {
            this.checkoutClick();
        });

        

        // Use event delegation for build box and edit box buttons
        document.addEventListener('click', (e) => {

            const nextStepBtn = e.target.closest('[data-next-step]');
            const previousStepBtn = e.target.closest('[data-previous-step]');
            const addToCartBtn = e.target.closest('[data-add-product-btn]');

            if (nextStepBtn) {
                this.handleNextStepClick(nextStepBtn);
            } else if (previousStepBtn) {
                this.handlePreviousStepClick();
            } else if (addToCartBtn) {
                e.preventDefault();
                this.handleAddToCartClick(addToCartBtn);
            }
        });

        // Set up quantity button handlers for all quantity containers
        const setupQuantityHandlers = () => {
            const allQtyContainers = this.modal.querySelectorAll('[data-qty-container]');
            allQtyContainers.forEach(container => {
                this._setupQuantityButtonHandlers(container);
            });
        };

        // Set up initial handlers
        setupQuantityHandlers();

        // Set up mutation observer to handle dynamically added quantity containers
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList') {
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === 1) { // Element node
                            // Check if the node itself is a qty container
                            if (node.matches && node.matches('[data-qty-container]')) {
                                this._setupQuantityButtonHandlers(node);
                            }
                            // Find ALL qty containers within the added node (not just the first)
                            const qtyContainers = node.querySelectorAll('[data-qty-container]');
                            qtyContainers.forEach(qtyContainer => {
                                this._setupQuantityButtonHandlers(qtyContainer);
                            });
                        }
                    });
                }
            });
        });

        // Start observing the modal for changes
        if (this.modal) {
            observer.observe(this.modal, {
                childList: true,
                subtree: true
            });
        }

        // Listen for deferred content loads to rebind events and update variants
        document.addEventListener('buildbox:content-loaded', (e) => {
            const { container, contentType, step } = e.detail || {};

            if (contentType === 'customization' && container) {
                // Rebind quantity handlers for the newly loaded content
                this.bindProductEvents(container);

                // Check if free limit is reached and switch to NMC+ if needed
                const currentStep = this.modal?.querySelector('[customization-data-step]:not(.d-none)');
                if (currentStep) {
                    const stepIndex = parseInt(currentStep.getAttribute('customization-data-step')) - 1;
                    const freeLimit = currentStep.querySelector('[data-free-product-limit]')?.getAttribute('data-free-product-limit');

                    if (this._hasReachedFreeProductLimit(stepIndex, freeLimit)) {
                        // Switch newly loaded products to NMC+ variant
                        this.updateToNMCPlus('nmc+');
                    }
                }
            }
        });
    }

    getCookie(name) {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
        return null;
    }

    _setCookie(name, value, days) {
        var date, expires;
        if (days) {
            date = new Date();
            date.setTime(date.getTime()+(days*24*60*60*1000));
            expires = "; expires="+date.toGMTString();
        } else {
            expires = "";
        }
        document.cookie = name+"="+value+expires+"; path=/";
    }

    showMessage(message){
        const messageSettings = this.modal.querySelector('[data-message-settings]');
        if(messageSettings){
            if(message == 'direct'){
                const allMessage = messageSettings.querySelectorAll('[data-all-message]');
                allMessage.forEach(message => {
                    message.classList.add('d-none');
                });
                messageSettings.classList.remove('d-none');
                messageSettings.querySelector('[data-message-direct]').classList.remove('d-none');
                this.hideFunnelAllSteps();
            }else if(message == 'processed'){
                const allMessage = messageSettings.querySelectorAll('[data-all-message]');
                allMessage.forEach(message => {
                    message.classList.add('d-none');
                });
                messageSettings.classList.remove('d-none');
                messageSettings.querySelector('[data-message-proccessed]').classList.remove('d-none');
                this.hideFunnelAllSteps();
            }else if(message == 'cookie'){
                const allMessage = messageSettings.querySelectorAll('[data-all-message]');
                allMessage.forEach(message => {
                    message.classList.add('d-none');
                });
                messageSettings.classList.remove('d-none');
                messageSettings.querySelector('[data-message-cookie]').classList.remove('d-none');
                this.hideFunnelAllSteps();
            }else if(message == 'success-paid'){
                const allMessage = messageSettings.querySelectorAll('[data-all-message]');
                allMessage.forEach(message => {
                    message.classList.add('d-none');
                });
                messageSettings.classList.remove('d-none');
                messageSettings.querySelector('[data-message-success-paid]').classList.remove('d-none');
                this.hideFunnelAllSteps();
            }else if(message == 'success'){
                const allMessage = messageSettings.querySelectorAll('[data-all-message]');
                allMessage.forEach(message => {
                    message.classList.add('d-none');
                });
                messageSettings.classList.remove('d-none');
                messageSettings.querySelector('[data-message-success]').classList.remove('d-none');
                this.hideFunnelAllSteps();
            }
        }
    }

    hideMessage(){
        const messageSettings = this.modal.querySelector('[data-message-settings]');
        if(messageSettings){
            messageSettings.classList.add('d-none');
        }
    }

    renderPreviewBanner(detail) {
        const render = () => {
            if (document.getElementById('preview-mode-banner')) return;
            const bar = document.createElement('div');
            bar.id = 'preview-mode-banner';
            bar.textContent = `⚠️ PREVIEW MODE — ${detail} · not real data`;
            bar.style.cssText = [
                'position:fixed','top:0','left:0','right:0',
                'z-index:2147483647','padding:6px 12px',
                'background:#ffeb3b','color:#000',
                'font:600 13px/1.4 system-ui,sans-serif',
                'text-align:center','letter-spacing:0.3px',
                'pointer-events:none','box-shadow:0 1px 4px rgba(0,0,0,0.2)',
            ].join(';');
            document.body.appendChild(bar);
        };
        if (document.body) render();
        else document.addEventListener('DOMContentLoaded', render, { once: true });
    }

    hideFunnelAllSteps(){
        this.customizationDataStep.forEach(step => {
            step.classList.add('d-none');
        });
        this.modal.querySelector('[data-bottom-bar-parent]').classList.add('d-none');
    }

    initializeFunnel(customerId, orderId){
        // this.showCustomizationStep(1);
        
        // Fetch order details
        fetch(`https://apps01.beachly.com/api/orders?order_id=${orderId}&shop=${window.Shopify.shop}`)
            .then(response => response.json())
            .then(data => {
                // console.log('Order data:', data);
                data.order.line_items.forEach((item) => {
                    let itemTitle = item.title.toLowerCase();
                    console.log('Processing item:', itemTitle);
                    
                    if (itemTitle.indexOf("beachly women's annual") != -1 || itemTitle.indexOf("beachly women's seasonal") != -1) {
                        if (item.variant_title != undefined) {
                            this.purchasedVariantSize = item.variant_title.toLowerCase();
                        }
                    }
                    
                    // Detect subscription type from item title
                    if (itemTitle.indexOf("home") != -1) {
                        this.subscriptionType = 'subscription_type_home';
                        console.log('Detected HOME subscription type');
                    } else if (itemTitle.indexOf("beachly men") != -1) {
                        this.subscriptionType = 'subscription_type_men';
                        console.log('Detected MEN subscription type');
                    } else if (itemTitle.indexOf("beauty") != -1) {
                        this.subscriptionType = 'subscription_type_beauty';
                        console.log('Detected BEAUTY subscription type');
                    } else if (itemTitle.indexOf("women") != -1 || itemTitle.indexOf("beachly") != -1) {
                        this.subscriptionType = 'subscription_type_women';
                        console.log('Detected WOMEN subscription type');
                    }
                });
                
                // Switch collections based on subscription type
                if (this.subscriptionType) {
                    console.log('Detected subscription type:', this.subscriptionType);
                    console.log('Calling switchToSubscriptionCollection...');
                    this.switchToSubscriptionCollection(this.subscriptionType);
                } else {
                    console.log('No subscription type detected');
                }

                const getHours = this.getHoursDiff(new Date(), new Date(data.order.created_at));
                const customizationWindowLimit = this.modal.querySelector('[data-customization-window-limit]')?.getAttribute('data-customization-window-limit') || '24';

                fetch('https://apps01.beachly.com/api/gift-subscribe-status', {
                    method: 'POST',
                    headers: this.APIHeader,
                    body: JSON.stringify({
                        "order_id": Number(orderId),
                        shop: window.Shopify.shop
                    })
                })
                .then(response => response.json())
                .then(dataInner => {
                    // console.log('dataInner', dataInner);
                    if(dataInner.message == false){
                        this.showMessage('processed');
                    // COMMENT TO ENABLE CUSTOMIZATION FOR AN INVALID ORDER: Commented in order to enable customization for an invalid order
                    }else if(getHours > Number(customizationWindowLimit)){
                        this.showMessage('cookie');
                    }else{
                        if (this.purchasedVariantSize != undefined && this.purchasedVariantSize != '' && this.purchasedVariantSize != 'default title') {
                            let findTag = `nmc_hidefrom_${this.purchasedVariantSize}`;
                            
                            // Get all product grids
                            const productGrids = this.modal.querySelectorAll('[data-grid-pro-id]');
                            // let productJsonData = [];

                            // Iterate through each product grid
                            productGrids.forEach(grid => {
                                const productId = grid.getAttribute('data-grid-pro-id');
                                const productJsonScript = grid.querySelector(`script[data-productJSON]`);
                                
                                if (productJsonScript) {
                                    const productData = JSON.parse(productJsonScript.textContent);
                                    let productTags = '';
                                    if (productData.tags != undefined && productData.tags != null && productData.tags != '') {
                                        productTags = productData.tags.map(element => {
                                            return element.toLowerCase();
                                        });
                                    }
                                    if (productTags.indexOf(findTag) > -1) {
                                        //==============Code for not size variants
                                        grid.classList.add('d-none');
                                    }
                                    // productJsonData.push(productData);
                                    // console.log('productData', productData);
                                }
                            });
                            // console.log('Product JSON Data:', productJsonData);
                        }
                        this.showCustomizationStep(1);
                    }
                })
                .catch(error => {
                    console.log('error', error);
                });

            })
            .catch(error => {
                console.log('error', error);
            });
        
    }

    getHoursDiff(startDate, endDate) {
        const msInHour = 1000 * 60 * 60;

        return Math.round(
            Math.abs(endDate.getTime() - startDate.getTime()) / msInHour,
        );
    }

    async handleNextStepClick(nextStepBtn) {
        const currentStep = this.modal.querySelector('[customization-data-step]:not(.d-none)');
        if (!currentStep) return;

        const currentStepNumber = parseInt(currentStep.getAttribute('customization-data-step'));
        const totalSteps = this.modal.querySelectorAll('[customization-data-step]').length;
        const bottomBarWrapper = this.modal.querySelector('[data-bottom-bar-wrapper]');

        // Get current step's array list
        const currentStepArray = this.productArray[currentStepNumber - 1] || [];

        if (currentStepNumber < totalSteps) {
            if(currentStepArray.length > 0){
                this.showCustomizationStep(currentStepNumber + 1);
            }
        } else if(currentStepNumber == totalSteps) {
            // console.log('this.productArray',this.productArray);
            let nmcArray = [];
            nextStepBtn.classList.add('loading');
            nextStepBtn.classList.add('disable-element');

            // CRITICAL: Validate all steps before submission and report to Sentry
            // Note: This is non-blocking - it logs to Sentry but does NOT prevent submission
            const allSteps = this.modal.querySelectorAll('[customization-data-step]');

            allSteps.forEach((step, index) => {
                const freeProductLimit = step.querySelector('[data-free-product-limit]')?.getAttribute('data-free-product-limit') || '0';
                this._validateAndReportFreeProductLimit(index, freeProductLimit, 'before_order_submit');
            });

            // Iterate through each step in productArray
            this.productArray.forEach(stepProducts => {
                if (stepProducts && stepProducts.length > 0) {
                    stepProducts.forEach(product => {
                        if (product.nmc_type === 'nmc') {
                            // Add to nmcArray with just variant_id
                            nmcArray.push({
                                "variant_id": Number(product.variant_id)
                            });
                        } else if (product.nmc_type === 'nmc+') {
                            // Add to nmcPlusArray with variant_id, quantity and properties
                            this.nmcPlusArray.push({
                                'id': Number(product.variant_id),
                                'quantity': Number(product.quantity),
                                'properties': {
                                    '_orderId': this.orderId
                                }
                            });
                        }
                    });
                }
            });

            // Check completion state and trigger Braze event if needed
            this._checkAndTriggerNMCCompletionEvent(totalSteps);

            // console.log('NMC Array:', nmcArray);
            // console.log('NMC+ Array:', this.nmcPlusArray);

            // this.showMessage('success');
            // this.showMessage('success-paid');

            fetch('https://apps01.beachly.com/api/gift-subscribe', {
                method: 'POST',
                headers: this.APIHeader,
                body: JSON.stringify({
                    "order_id": Number(this.orderId),
                    "type": 2,
                    "line_items": nmcArray,
                    shop: window.Shopify.shop
                })
            })
            .then(response => response.json())
            .then(data => {
                if(data.message == 'Your build your box gift request has been created successfully!'){
                    if(this.nmcPlusArray.length > 0){
                        this.showMessage('success-paid');
                    }else{
                        this.showMessage('success');
                    }
                }else if(data.message == 'Your gift has been processed already!'){
                    this.showMessage('processed');
                }
                nextStepBtn.classList.remove('loading');
                nextStepBtn.classList.remove('disable-element');
            })
            .catch(error => {
                console.log('error', error);
            });
        }

        // Hide remove buttons of the previous step before moving to next
        if (bottomBarWrapper && currentStepNumber > 0) {
            const previousStepList = bottomBarWrapper.querySelector(`ul[data-customized-step${currentStepNumber}]`);
            if (previousStepList) {
                const removeButtons = previousStepList.querySelectorAll('[data-remove-product]');
                removeButtons.forEach(btn => {
                    btn.classList.add('d-none');
                });
            }
        }
    }

    /**
     * Check if all NMC customization groups have at least one selection
     * @returns {boolean} True if all groups have selections
     */
    _isNMCustomizationComplete() {
        if (!this.productArray || this.productArray.length === 0) {
            return false;
        }
        
        // Check if all groups have at least one selection
        return this.productArray.every(step => step && step.length > 0);
    }

    /**
     * Check completion state and trigger Braze event when NMC customization is complete
     * Only fires event when transitioning from incomplete to complete
     */
    _checkAndTriggerNMCCompletionEvent(totalSteps) {
        const isNowComplete = this._isNMCustomizationComplete();
        
        // Only trigger event when transitioning from incomplete to complete
        if (!this.previousCompletionState && isNowComplete) {
            this._logBrazeNMCustomizationCompleteEvent(totalSteps);
        }
        
        // Update previous state
        this.previousCompletionState = isNowComplete;
    }

    /**
     * Log Braze event when NMC customization is complete
     */
    _logBrazeNMCustomizationCompleteEvent(totalSteps) {
        // Check if Braze SDK is available
        if (typeof braze === 'undefined' || typeof braze.logCustomEvent !== 'function') {
            console.warn('[NMC Customization] Braze SDK not available for completion event');
            return;
        }

        try {
            // Get NMC products from all groups
            const nmcProducts = [];
            let totalNmcProducts = 0;
            let totalNmcPlusProducts = 0;
            
            this.productArray.forEach((stepProducts, stepIndex) => {
                if (stepProducts && stepProducts.length > 0) {
                    stepProducts.forEach(product => {
                        if (product.nmc_type) {
                            if (product.nmc_type === 'nmc') {
                                totalNmcProducts++;
                            } else if (product.nmc_type === 'nmc+') {
                                totalNmcPlusProducts++;
                            }
                            
                            nmcProducts.push({
                                product_id: product.product_id || null,
                                variant_id: product.variant_id || null,
                                nmc_type: product.nmc_type,
                                quantity: product.quantity || 1
                            });
                        }
                    });
                }
            });

            // Get order information
            const orderId = this.orderId || null;
            const totalGroups = totalSteps || this.productArray.length;
            const completedGroups = this.productArray.filter(step => step && step.length > 0).length;

            // Prepare event properties
            const eventProperties = {
                order_id: orderId,
                total_groups: totalGroups,
                completed_groups: completedGroups,
                total_nmc_products: totalNmcProducts,
                total_nmc_plus_products: totalNmcPlusProducts,
                total_customization_items: nmcProducts.length,
                timestamp: new Date().toISOString()
            };

            // Log custom event to Braze
            braze.logCustomEvent('nmc_customization_complete', eventProperties);

            console.log('[NMC Customization] Braze event logged: nmc_customization_complete', eventProperties);
        } catch (error) {
            console.error('[NMC Customization] Error logging Braze completion event:', error);
            
            // Log to Sentry if available
            if (typeof Sentry !== 'undefined' && typeof Sentry.captureException === 'function') {
                Sentry.captureException(error, {
                    tags: {
                        feature: 'nmc_customization',
                        operation: 'braze_event_logging'
                    },
                    extra: {
                        order_id: this.orderId || null,
                        error_message: error.toString()
                    }
                });
            }
        }
    }

    checkoutClick(){
        let formData = {
            'items': this.nmcPlusArray
        }
        fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(formData)
        })
        .then(response => {
            console.log('added to cart')
            window.location.href = '/checkout';
            return response.json();
        })
        .catch((error) => {
            console.error('Error:', error);
        });
    }

    handlePreviousStepClick() {
        const currentStep = this.modal.querySelector('[customization-data-step]:not(.d-none)');
        if (!currentStep) return;

        const currentStepNumber = parseInt(currentStep.getAttribute('customization-data-step'));
        if(currentStepNumber == 1){
            this.closeModal();
            return;
        }
        const bottomBarWrapper = this.modal.querySelector('[data-bottom-bar-wrapper]');
        
        // Show remove buttons of the current step before moving to previous
        if (bottomBarWrapper && currentStepNumber > 0) {
            const currentStepList = bottomBarWrapper.querySelector(`ul[data-customized-step${currentStepNumber}]`);
            if (currentStepList) {
                // Remove the current step's product list from bottom bar
                currentStepList.remove();
            }
            const previousStepList = bottomBarWrapper.querySelector(`ul[data-customized-step${currentStepNumber - 1}]`);
            if (previousStepList) {
                const removeButtons = previousStepList.querySelectorAll('[data-remove-product]');
                removeButtons.forEach(btn => {
                    btn.classList.remove('d-none');    
                });
            }
        }
        
        if (currentStepNumber > 1) {
            // Clear the current step's array
            this.productArray[currentStepNumber - 1] = [];
            this.resetCurrentStepProductGrids();
            // Show data-selected-text elements
            const selectedTextElements = this.modal.querySelectorAll('[data-selected-text]');
            selectedTextElements.forEach(el => el.classList.remove('d-none'));

            // Hide plus texts
            const choicePlusText = this.modal.querySelector('[data-choice-plus-text]');
            const choicePlusSubtitle = this.modal.querySelector('[data-choice-plus-subtitle]');
            if (choicePlusText) choicePlusText.classList.add('d-none');
            if (choicePlusSubtitle) choicePlusSubtitle.classList.add('d-none');

            this.showCustomizationStep(currentStepNumber - 1);
        }
    }

    showCustomizationStep(stepNumber) {
        const currentStep = this.modal.querySelector(`[customization-data-step="${stepNumber}"]`);
        if (!currentStep) return;

        // Handle previous step button state
        const previousStepBtn = this.modal.querySelector('[data-previous-step]');
        if (previousStepBtn) {
            if (stepNumber === 1) {
                previousStepBtn.classList.add('disable-element');
            } else {
                previousStepBtn.classList.remove('disable-element');
            }
        }

        // Hide all steps
        this.customizationDataStep.forEach(step => {
            step.classList.add('d-none');
        });

        const steps = this.modal?.querySelectorAll('[customization-data-step]');
        const progressSteps = this.modal?.querySelectorAll('[customization-data-step-inner]');
        const bottomBarWrapper = this.modal?.querySelector('[data-bottom-bar-wrapper]');
        const nextStepBtn = this.modal.querySelector('[data-next-step]');
        const bottomBarParent = this.modal.querySelector('[data-bottom-bar-parent]');
        
        if(bottomBarParent){
            bottomBarParent.classList.remove('d-none');
        }
        // Hide mobile group titles of next steps
        if (bottomBarWrapper) {
            const allMobileTitles = bottomBarWrapper.querySelectorAll('[data-mobile-group-title]');
            allMobileTitles.forEach(title => {
                const titleStep = parseInt(title.getAttribute('data-mobile-group-title'));
                if (titleStep > stepNumber) {
                    title.classList.add('d-none');
                } else {
                    title.classList.remove('d-none');
                }
            });
        }

        if (steps) {
            steps.forEach((step, index) => {
                if (index + 1 === stepNumber) {
                    step.classList.remove('d-none');
                    // Get the free product limit for this step
                    const freeProductLimit = step.querySelector('[data-free-product-limit]')?.getAttribute('data-free-product-limit') || '0';
                    // Get the group title
                    const groupTitle = step.querySelector('[data-group-title]')?.getAttribute('data-group-title') || '';
                    
                    // Get current step's array length
                    const currentStepArray = this.productArray[stepNumber - 1] || [];
                    const selectedCount = currentStepArray.length;

                    // Update the selected text
                    const selectedTextElements = this.modal.querySelectorAll('[data-selected-text]');
                    selectedTextElements.forEach(element => {
                        element.textContent = `${selectedCount} of ${freeProductLimit} selected`;
                    });

                    // Check if all data-nmc-type elements are filled
                    const nmcTypeElements = step.querySelectorAll('[data-nmc-type]');
                    const filledNmcElements = step.querySelectorAll('[data-nmc-type].product-added');
                    const nmcCount = filledNmcElements.length;
                    const nmcPlusCount = step.querySelectorAll('[data-nmc-type].product-added[data-nmc-type="nmc+"]').length;
                    const isAllFilled = nmcTypeElements.length > 0 && nmcTypeElements.length === filledNmcElements.length;

                    // Update next step button state
                    if (nextStepBtn) {
                        if (isAllFilled) {
                            nextStepBtn.removeAttribute('disabled');
                            nextStepBtn.classList.remove('disable-element');
                            bottomBarParent.classList.add('choice-plus');
                            // Show plus tag for current step
                            const currentStep = this.modal.querySelector('[customization-data-step]:not(.d-none)');
                            const plusTag = currentStep.querySelector('[data-plus-tag]');
                            if (plusTag) plusTag.classList.remove('d-none');
                        } else {
                            nextStepBtn.setAttribute('disabled', 'disabled');
                            nextStepBtn.classList.add('disable-element');
                            bottomBarParent.classList.remove('choice-plus');
                            // Hide plus tag for current step
                            const currentStep = this.modal.querySelector('[customization-data-step]:not(.d-none)');
                            const plusTag = currentStep.querySelector('[data-plus-tag]');
                            if (plusTag) plusTag.classList.add('d-none');
                        }
                    }

                    // Update the bottom bar wrapper content
                    if (bottomBarWrapper) {
                        // Check if product list exists for current step
                        const existingProductList = bottomBarWrapper.querySelector(`[data-customized-step${stepNumber}]`);
                        
                        // Check if mobile group title exists for current step
                        const existingMobileTitle = bottomBarWrapper.querySelector(`[data-mobile-group-title="${stepNumber}"]`);
                        
                        if (!existingMobileTitle) {
                            // Add new mobile group title span if it doesn't exist
                            const mobileGroupTitleSpan = document.createElement('span');
                            mobileGroupTitleSpan.className = 'd-block d-md-none fs-xs fw-medium ls-2 mb-3 mt-3 text-uppercase';
                            mobileGroupTitleSpan.setAttribute('data-mobile-group-title', stepNumber);
                            mobileGroupTitleSpan.textContent = groupTitle;
                            
                            // Insert title before the product list if it exists
                            if (existingProductList) {
                                bottomBarWrapper.insertBefore(mobileGroupTitleSpan, existingProductList);
                            } else {
                                bottomBarWrapper.appendChild(mobileGroupTitleSpan);
                            }
                        } else {
                            // Update existing title text
                            existingMobileTitle.textContent = groupTitle;
                        }

                        // Only create product list if it doesn't exist
                        if (!existingProductList) {
                            // Create and append the product list for current step
                            const productList = document.createElement('ul');
                            // Add divider class for all steps except the first one
                            productList.className = `customized-step${stepNumber} added-product-list flex-wrap align-items-center d-flex list-unstyled m-0 p-0 ${stepNumber > 1 ? 'divider' : ''}`;
                            productList.setAttribute(`data-customized-step${stepNumber}`, '');
                            
                            // Insert product list after the title
                            const title = bottomBarWrapper.querySelector(`[data-mobile-group-title="${stepNumber}"]`);
                            if (title) {
                                bottomBarWrapper.insertBefore(productList, title.nextSibling);
                            } else {
                                bottomBarWrapper.appendChild(productList);
                            }

                            // Add empty product items based on free product limit
                            for (let i = 0; i < parseInt(freeProductLimit); i++) {
                                const listItem = document.createElement('li');
                                listItem.setAttribute('data-nmc-type', '');
                                listItem.className = 'added-product-item';
                                listItem.innerHTML = `
                                    <div class="added-product-image">
                                        <img src="https://cdn.shopify.com/s/files/1/1336/5077/files/Products_Sticky_Bar_Account.svg?v=1746096804" 
                                            alt="blank image" 
                                            class="h-100 object-fit-contain w-100"
                                            width="100"
                                            height="100">
                                    </div>
                                `;
                                productList.appendChild(listItem);
                            }
                        }

                        // Get the choice plus elements
                        const choicePlusText = this.modal.querySelector('[data-choice-plus-text]');
                        const choicePlusSubtitle = this.modal.querySelector('[data-choice-plus-subtitle]');
                        const selectedTextElements = this.modal.querySelectorAll('[data-selected-text]');

                        // Update visibility of choice plus and selected text elements
                        if (nmcCount === parseInt(freeProductLimit) && nmcPlusCount === 0) {
                            // When all NMC+ products are removed, show choice plus elements
                            if (choicePlusText) choicePlusText.classList.remove('d-none');
                            if (choicePlusSubtitle) choicePlusSubtitle.classList.remove('d-none');
                            selectedTextElements.forEach(element => element.classList.add('d-none'));
                        } else if(nmcCount === parseInt(freeProductLimit) && nmcPlusCount > 0) {
                            // When all products are filled and at least one is NMC+, hide choice plus and show selected text
                            if (choicePlusText) choicePlusText.classList.add('d-none');
                            if (choicePlusSubtitle) choicePlusSubtitle.classList.add('d-none');
                            selectedTextElements.forEach(element => {
                                element.classList.remove('d-none');
                                element.textContent = `${nmcCount} of ${freeProductLimit} selected`;
                            });
                        } else if (nmcCount <= parseInt(freeProductLimit)) {
                            // When below limit, show selected text and hide choice plus
                            selectedTextElements.forEach(element => {
                                element.classList.remove('d-none');
                                element.textContent = `${nmcCount} of ${freeProductLimit} selected`;
                            });
                            if (choicePlusText) choicePlusText.classList.add('d-none');
                            if (choicePlusSubtitle) choicePlusSubtitle.classList.add('d-none');
                        }
                    }
                } else {
                    step.classList.add('d-none');
                }
            });
        }

        if (progressSteps) {
            progressSteps.forEach((step) => {
                // Remove all active and completed classes first
                step.classList.remove('active', 'completed');
                
                // Get the step number from the data attribute
                const stepInnerNumber = parseInt(step.getAttribute('customization-data-step-inner'));
                
                if (stepInnerNumber < stepNumber) {
                    // Previous steps are completed
                    step.classList.add('completed');
                } else if (stepInnerNumber === stepNumber) {
                    // Current step is active
                    step.classList.add('active');
                }
            });
        }
    }

    resetCurrentStepProductGrids() {
        const currentStep = this.modal.querySelector('[customization-data-step]:not(.d-none)');
        if (!currentStep) return;
        const productGrids = currentStep.querySelectorAll('[data-product-grid]');
        productGrids.forEach(productGrid => {
            productGrid.classList.remove('product-added');
            const productForm = productGrid.querySelector('product-form');
            if (productForm) {
                this.updateToNMCPlus('nmc', productForm);
            }
        });
    }

    resetAllSteps() {
        // Get all customization steps
        const steps = this.modal.querySelectorAll('[customization-data-step]');
        const bottomBarWrapper = this.modal.querySelector('[data-bottom-bar-wrapper]');

        // Reset each step
        steps.forEach((step, index) => {
            const stepNumber = index + 1;
            
            // Reset product array for this step
            this.productArray[stepNumber - 1] = [];
            
            // Reset product grids for this step
            const productGrids = step.querySelectorAll('[data-product-grid]');
            productGrids.forEach(grid => {
                grid.classList.remove('product-added');
                const productForm = grid.querySelector('product-form');
                if (productForm) {
                    // Find and check NMC radio button
                    const variantRadios = productForm.querySelector('variant-radios');
                    if (variantRadios) {
                        const nmcRadio = variantRadios.querySelector('input[value="NMC"]');
                        if (nmcRadio) {
                            nmcRadio.checked = true;
                            // Trigger change event to update variant
                            nmcRadio.dispatchEvent(new Event('change', { bubbles: true }));
                            
                            // Set current variant to first default variant
                            const variantSelects = productForm.querySelector('variant-selects');
                            if (variantSelects) {
                                const variants = JSON.parse(variantSelects.querySelector('[data-variantJSON]').textContent);
                                const defaultVariant = variants.find(variant => 
                                    variant.options.some(option => option.toLowerCase() === 'nmc')
                                );
                                if (defaultVariant) {
                                    variantSelects.currentVariant = defaultVariant;
                                }
                            }
                        }
                    }

                    const quantityInput = productForm.querySelector('[data-qty-input]');
                    if (quantityInput) {
                        quantityInput.value = 0;
                        quantityInput.closest('[data-qty-container]').classList.add('d-none');
                    }
                    this.updateToNMCPlus('nmc', productForm);
                }
            });

            // Reset bottom bar for this step
            if (bottomBarWrapper) {
                const stepList = bottomBarWrapper.querySelector(`ul[data-customized-step${stepNumber}]`);
                if (stepList) {
                    stepList.remove();
                }
            }

            // Reset selected text and plus text visibility
            const selectedTextElements = step.querySelectorAll('[data-selected-text]');
            selectedTextElements.forEach(el => {
                el.classList.remove('d-none');
                el.textContent = `0 of ${step.querySelector('[data-free-product-limit]')?.getAttribute('data-free-product-limit') || '0'} selected`;
            });

            const choicePlusText = step.querySelector('[data-choice-plus-text]');
            const choicePlusSubtitle = step.querySelector('[data-choice-plus-subtitle]');
            if (choicePlusText) choicePlusText.classList.add('d-none');
            if (choicePlusSubtitle) choicePlusSubtitle.classList.add('d-none');
        });

        // Clear the bottom bar wrapper
        if (bottomBarWrapper) {
            bottomBarWrapper.innerHTML = '';
        }

        // Reset customized one-times array
        this.customizedOneTimes = [];
    }

    openModal() {
        if (!this.modal) return;
        this.modal.classList.add('open');
        this.body.classList.add('scroll-fixed');
        this.body.style.overflowY = 'hidden';
        if (this.siteOverlay) {
            // this.siteOverlay.classList.add('overlay--body');
        }

        // Reset scroll position of modal body
        const modalBody = this.modal.querySelector('.modal-body');
        if (modalBody) {
            modalBody.scrollTop = 0;
        }
    }

    closeModal() {
        if (!this.modal) return;
        this.modal.classList.remove('open');
        this.body.classList.remove('scroll-fixed');
        this.body.style.overflowY = 'auto';

        // Reset scroll position of modal body
        const modalBody = this.modal.querySelector('.modal-body');
        if (modalBody) {
            modalBody.scrollTop = 0;
        }
        const getTabs = document.querySelectorAll('[data-sale-step]');
        getTabs.forEach(tab => {
            tab.classList.remove('active');
        });
    }

    _setupQuantityButtonHandlers(qtyContainer) {
        if (!qtyContainer) return;

        // Prevent duplicate handler binding
        if (qtyContainer.hasAttribute('data-handlers-bound')) return;
        qtyContainer.setAttribute('data-handlers-bound', 'true');

        const decreaseBtn = qtyContainer.querySelector('[data-min-btn]');
        const increaseBtn = qtyContainer.querySelector('[data-plus-btn]');
        const qtyInput = qtyContainer.querySelector('[data-qty-input]');
        const productCard = qtyContainer.closest('[data-product-grid]');
        const removeBtn = productCard?.querySelector('[data-remove-product-btn]');

        const productId = productCard?.getAttribute('data-product-grid');
        console.log('[NMC Selection] Setting up quantity handlers for product:', productId, {
            decreaseBtnFound: !!decreaseBtn,
            increaseBtnFound: !!increaseBtn,
            removeBtnFound: !!removeBtn
        });

        if (decreaseBtn) {
            decreaseBtn.addEventListener('click', (e) => {
                e.preventDefault();
                console.log('[NMC Selection] Decrease button clicked for product:', productId);
                this._handleQuantityRemove(productCard);
            });
        }

        if (removeBtn) {
            removeBtn.addEventListener('click', (e) => {
                e.preventDefault();
                console.log('[NMC Selection] Remove button clicked for product:', productId);
                this._handleQuantityRemove(productCard);
            });
        }

        if (increaseBtn) {
            increaseBtn.addEventListener('click', (e) => {
                e.preventDefault();
                const currentQty = parseInt(qtyInput.value);
                qtyInput.value = currentQty + 1;
                this.handleAddToCartClick(increaseBtn);
            });
        }
    }

    _handleQuantityRemove(productCard){
        const currentStep = this.modal.querySelector('[customization-data-step]:not(.d-none)');
        if (!currentStep) {
            console.warn('[NMC Selection] _handleQuantityRemove: currentStep not found');
            return;
        }

        const currentStepNumber = currentStep.getAttribute('customization-data-step');
        const productGridId = productCard.getAttribute('data-product-grid');
        const currentStepList = this.modal.querySelector(`[data-customized-step${currentStepNumber}]`);
        const removeBtn = currentStepList?.querySelector(`[data-remove-product="${productGridId}"]`);

        console.log('[NMC Selection] _handleQuantityRemove called', {
            productGridId,
            currentStepNumber,
            currentStepListFound: !!currentStepList,
            removeBtnFound: !!removeBtn
        });

        if (removeBtn) {
            removeBtn.click();
        } else {
            console.warn('[NMC Selection] Remove button not found in bottom bar!', {
                productGridId,
                currentStepNumber,
                expectedSelector: `[data-remove-product="${productGridId}"]`
            });
        }
    }

    /**
     * Public method to bind product events for dynamically loaded content.
     * Called from build-box-deferred.js after deferred content is inserted.
     * @param {HTMLElement} container - The container with newly loaded products
     */
    bindProductEvents(container) {
        if (!container) return;

        // SAFETY CHECK: Only bind if container is within the customization modal
        if (!this.modal || !this.modal.contains(container)) {
            console.log('[FunnelCustomization] Skipping bindProductEvents - container not in customization modal');
            return;
        }

        // Set up quantity handlers for all quantity containers within the new content
        const qtyContainers = container.querySelectorAll('[data-qty-container]');
        qtyContainers.forEach(qtyContainer => {
            this._setupQuantityButtonHandlers(qtyContainer);
        });

        console.log('[FunnelCustomization] Bound events for', qtyContainers.length, 'quantity containers');
    }

    updateToNMCPlus(type, productForm) {
        // Get current step
        const currentStep = this.modal.querySelector('[customization-data-step]:not(.d-none)');
        if (!currentStep) return;

        // CORRECTED: Only switch variant selection for unadded products
        // Products already added keep their original variant selection
        const productGrids = currentStep.querySelectorAll('[data-product-grid]:not(.product-added)');
        productGrids.forEach(productGrid => {
            const variantRadios = productGrid.querySelector('variant-radios');
            if (!variantRadios) return;
            const variantRadiosInputs = variantRadios.querySelectorAll('input[type="radio"]');

            if (type == 'nmc+') {
                // Switch to NMC+ variant for unadded products
                variantRadiosInputs.forEach(input => {
                    const option1 = input.value?.toLowerCase() || '';
                    const option2 = input.value?.toLowerCase() || '';
                    const option3 = input.value?.toLowerCase() || '';

                    if (option1.includes('nmc+') || option2.includes('nmc+') || option3.includes('nmc+')) {
                        input.checked = true;
                        input.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                });
            }
            if (type == 'nmc') {
                // Switch to NMC variant for unadded products
                variantRadiosInputs.forEach(input => {
                    const option1 = input.value?.toLowerCase() || '';
                    const option2 = input.value?.toLowerCase() || '';
                    const option3 = input.value?.toLowerCase() || '';

                    if ((option1.includes('nmc') || option2.includes('nmc') || option3.includes('nmc')) &&
                        !(option1.includes('nmc+') || option2.includes('nmc+') || option3.includes('nmc+'))) {
                        input.checked = true;
                        input.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                });
            }
        });

        console.log('[NMC Selection] Updated variant selection for unadded products to:', type);
    }

    handleAddToCartClick(btn) {

        // Get current step details
        const currentStep = this.modal.querySelector('[customization-data-step]:not(.d-none)');
        if (!currentStep) return;

        const currentStepNumber = parseInt(currentStep.getAttribute('customization-data-step'));
        const collectionHandle = currentStep.querySelector('[data-customization-collection-handle]')?.getAttribute('data-customization-collection-handle');
        const freeProductLimit = currentStep.querySelector('[data-free-product-limit]')?.getAttribute('data-free-product-limit') || '0';
        const stepIndex = currentStepNumber - 1;

        const form = btn.closest('form');
        if (!form) return;

        // Get product form instance
        const productForm = form.closest('product-form');
        if (!productForm) return;

        // Get product details
        const productId = form.getAttribute('data-product-id');
        var quantity = productForm.querySelector('[data-qty-input]')?.value || 1;
        if(quantity == 0) quantity = 1;
        // console.log('quantity', quantity,productForm.querySelector('[data-qty-input]'));

        // Get product image
        const productImg = form.querySelector('[data-feauredImage]')?.getAttribute('data-feauredImage') || '';

        // Get the filled items count for current step
        const stepList = this.modal.querySelector(`[data-customized-step${currentStepNumber}]`);

        // Get variant data from the form
        const variantRadios = productForm.querySelector('variant-radios');
        if (!variantRadios) return;

        const variantRadiosInputs = variantRadios.querySelectorAll('input[type="radio"]');

        // Get currentVariant from VariantSelects class
        const currentVariant = variantRadios.currentVariant;
        if (!currentVariant) return;

        // Determine which variant to use based on filled items count
        let selectedVariantId = currentVariant.id;
        // console.log('currentVariant',currentVariant);

        // Check variant options for NMC or NMC+
        const option1 = currentVariant.option1?.toLowerCase() || '';
        const option2 = currentVariant.option2?.toLowerCase() || '';
        const option3 = currentVariant.option3?.toLowerCase() || '';

        // Add NMC type property if found
        let nmcType = null;
        if (option1.includes('nmc') || option2.includes('nmc') || option3.includes('nmc')) {
            if (option1.includes('nmc+') || option2.includes('nmc+') || option3.includes('nmc+')) {
                nmcType = 'nmc+';
            } else {
                nmcType = 'nmc';
            }
        }

        // Check current counts to determine if we can add as NMC or must use NMC+
        const currentCounts = this._getStepProductCounts(stepIndex);

        // CRITICAL FIX: Only block NMC products when NMC limit is reached
        // NMC+ products are UNLIMITED and should never be blocked
        if (nmcType === 'nmc' && currentCounts.nmcCount >= parseInt(freeProductLimit)) {
            this._logNMCSelectionState('BLOCKED - NMC Limit Reached', stepIndex, freeProductLimit);
            console.warn('[NMC Selection] Cannot add NMC product - free limit reached! Switch to NMC+ to add more.', {
                productId: productId,
                attemptedType: nmcType,
                nmcCount: currentCounts.nmcCount,
                limit: freeProductLimit
            });
            return; // Prevent adding NMC products beyond limit
        }

        // Store subscription in productArray for current step
        if (this.productArray[stepIndex]) {

            // IMPORTANT: Check if this EXACT variant with the SAME nmc_type exists
            // This allows the same product to be added as BOTH NMC and NMC+
            const existingProductIndex = this.productArray[stepIndex].findIndex(
                item => String(item.external_variant_id.ecommerce) === String(selectedVariantId)
                    && item.nmc_type === nmcType
            );

            if (existingProductIndex !== -1) {
                // If exact variant+type combination exists, increase quantity
                this.productArray[stepIndex][existingProductIndex].quantity += 1;
                console.log('[NMC Selection] Increased quantity for existing product', {
                    productId: productId,
                    type: nmcType,
                    newQuantity: this.productArray[stepIndex][existingProductIndex].quantity
                });
            } else {
                // CRITICAL FIX: When adding a new NMC type (NMC or NMC+) for a product,
                // always start with quantity 1, regardless of the input value
                // The input shows the total for the OTHER type (e.g., NMC count when adding NMC+)
                this.productArray[stepIndex].push({
                    addon_img: productImg,
                    quantity: 1, // Always start with 1 for new type
                    variant_id: String(selectedVariantId),
                    product_id: String(productId),
                    external_variant_id: { "ecommerce": String(selectedVariantId)},
                    price: currentVariant.price,
                    nmc_type: nmcType
                });
                console.log('[NMC Selection] Added new product', {
                    productId: productId,
                    type: nmcType,
                    quantity: 1
                });
            }
        }

        // CRITICAL FIX: Switch variant mode BEFORE updating bottom bar
        // This ensures button text is correct after mode switch
        if (nmcType === 'nmc') {
            // Check if we've now reached the NMC limit
            const countsAfterAdd = this._getStepProductCounts(stepIndex);
            if (countsAfterAdd.nmcCount >= parseInt(freeProductLimit)) {
                this.updateToNMCPlus('nmc+');
                console.log('[NMC Selection] NMC limit reached, switching unadded products to NMC+');
            }
        }

        // Update the product list in the bottom bar (after mode switch)
        this._updateBottomBarProductList(productForm, productImg, currentVariant);

        // Log the current state after adding
        this._logNMCSelectionState('Product Added', stepIndex, freeProductLimit);

        // Validate free product limit after adding (safety check for any bugs)
        this._validateAndReportFreeProductLimit(stepIndex, freeProductLimit, 'after_product_add');
    }

    checkedAfterAdd(step){
        // DEPRECATED: This function is no longer needed as mode switching
        // is now handled directly in handleAddToCartClick
        // Keeping for compatibility but it does nothing
        console.log('[NMC Selection] checkedAfterAdd called (deprecated)');
    }

    _updateBottomBarProductList(productForm, productImg, currentVariant,step) {
        var currentStep = step;
        if(!currentStep){
            currentStep = this.modal.querySelector('[customization-data-step]:not(.d-none)');
        }
        const freeProductLimit = currentStep.querySelector('[data-free-product-limit]')?.getAttribute('data-free-product-limit') || '0';
        const currentStepNumber = parseInt(currentStep.getAttribute('customization-data-step'));
        const stepIndex = currentStepNumber - 1;
        const groupTitle = currentStep.querySelector('[data-group-title]')?.getAttribute('data-group-title') || '';

        const bottomBarWrapper = this.modal.querySelector('[data-bottom-bar-wrapper]');
        const nextStepBtn = this.modal.querySelector('[data-next-step]');
        const bottomBarParent = this.modal.querySelector('[data-bottom-bar-parent]');
        if (!bottomBarWrapper) return;

        // Find or create the list for current step
        let stepList = bottomBarWrapper.querySelector(`[data-customized-step${currentStepNumber}]`);
        if (!stepList) {
            stepList = document.createElement('ul');
            stepList.className = `customized-step${currentStepNumber} added-product-list flex-wrap align-items-center d-flex list-unstyled m-0 p-0 ${currentStepNumber > 1 ? 'divider' : ''}`;
            stepList.setAttribute(`data-customized-step${currentStepNumber}`, '');
            bottomBarWrapper.appendChild(stepList);
        }

        // Clear existing list items
        stepList.innerHTML = '';

        // Update the bottom bar wrapper content
        if (bottomBarWrapper) {
            // Check if product list exists for current step
            const existingProductList = bottomBarWrapper.querySelector(`[data-customized-step${currentStepNumber}]`);
            
            // Check if mobile group title exists for current step
            const existingMobileTitle = bottomBarWrapper.querySelector(`[data-mobile-group-title="${currentStepNumber}"]`);
            
            if (!existingMobileTitle) {
                // Add new mobile group title span if it doesn't exist
                const mobileGroupTitleSpan = document.createElement('span');
                mobileGroupTitleSpan.className = 'd-block d-md-none fs-xs fw-medium ls-2 mb-3 mt-3 text-uppercase';
                mobileGroupTitleSpan.setAttribute('data-mobile-group-title', currentStepNumber);
                mobileGroupTitleSpan.textContent = groupTitle;
                
                // Insert title before the product list if it exists
                if (existingProductList) {
                    bottomBarWrapper.insertBefore(mobileGroupTitleSpan, existingProductList);
                } else {
                    bottomBarWrapper.appendChild(mobileGroupTitleSpan);
                }
            } else {
                // Update existing title text
                existingMobileTitle.textContent = groupTitle;
            }

            // Only create product list if it doesn't exist
            if (!existingProductList) {
                // Create and append the product list for current step
                const productList = document.createElement('ul');
                // Add divider class for all steps except the first one
                productList.className = `customized-step${currentStepNumber} added-product-list flex-wrap align-items-center d-flex list-unstyled m-0 p-0 ${currentStepNumber > 1 ? 'divider' : ''}`;
                productList.setAttribute(`data-customized-step${currentStepNumber}`, '');
                
                // Insert product list after the title
                const title = bottomBarWrapper.querySelector(`[data-mobile-group-title="${currentStepNumber}"]`);
                if (title) {
                    bottomBarWrapper.insertBefore(productList, title.nextSibling);
                } else {
                    bottomBarWrapper.appendChild(productList);
                }
            }
        }

        // Get filled items count from this.productArray using helper method
        const counts = this._getStepProductCounts(stepIndex);
        const nmcCount = counts.nmcCount;
        const nmcPlusCount = counts.nmcPlusCount;

        // First, add NMC products
        if (this.productArray[stepIndex]) {
            this.productArray[stepIndex].forEach(product => {
                if (product.nmc_type) {
                    if (product.nmc_type === 'nmc') {
                    const productItem = document.createElement('li');
                    productItem.className = 'added-product-item has-product';
                    productItem.setAttribute('data-nmc-type', '');

                    const productImage = product.addon_img;
                    const productId = product.product_id;
                    const quantity = product.quantity || 1;

                    productItem.innerHTML = `
                        <div class="added-product-image">
                            <img src="${productImage}" alt="${product.product_title}" class="h-100 object-fit-cover option-image w-100">
                            <a class="align-items-center bg-primary d-flex justify-content-center position-absolute remove-added-product rounded-circle text-white" href="#" data-remove-product="${productId}">
                                <svg width="10" height="2" viewBox="0 0 10 2" aria-hidden="true" focusable="false">
                                    <use href="#icon-minus-thick"></use>
                                </svg>
                            </a>
                            ${quantity > 1 ? `<a class="align-items-center bg-primary d-flex justify-content-center position-absolute remove-added-product-quantity rounded-circle text-white" href="javascript:void(0)">
                                ${quantity}
                            </a>` : ''}
                        </div>
                    `;

                    // Add click handler for remove button
                    const removeBtn = productItem.querySelector('[data-remove-product]');
                    if (removeBtn) {
                        removeBtn.addEventListener('click', (e) => {
                            e.preventDefault();
                            const productId = removeBtn.getAttribute('data-remove-product');
                            this.removeProductFromArray(currentStepNumber, stepIndex, freeProductLimit, productForm, productItem, currentStep, productId);
                        });
                    }

                    stepList.appendChild(productItem);
                }
            }
            });
        }

        // Add empty slots for remaining NMC products
        const remainingNmcSlots = parseInt(freeProductLimit) - nmcCount;
        for (let i = 0; i < remainingNmcSlots; i++) {
            const emptyItem = document.createElement('li');
            emptyItem.className = 'added-product-item';
            emptyItem.setAttribute('data-nmc-type', '');
            emptyItem.innerHTML = `
                <div class="added-product-image">
                    <img src="https://cdn.shopify.com/s/files/1/1336/5077/files/Products_Sticky_Bar_Account.svg?v=1746096804" 
                        alt="blank image" 
                        class="h-100 object-fit-contain w-100"
                        width="100"
                        height="100">
                </div>
            `;
            stepList.appendChild(emptyItem);
        }

        // Then, add NMC+ products
        if (this.productArray[stepIndex]) {
            this.productArray[stepIndex].forEach(product => {
                if (product.nmc_type) {
                    if (product.nmc_type === 'nmc+') {
                        const productItem = document.createElement('li');
                        productItem.className = 'added-product-item has-product';
                        productItem.setAttribute('data-nmc-plus-type', '');

                        // Get image from properties
                        const productImage = product.addon_img;
                        const productId = product.product_id;
                        const quantity = product.quantity || 1;
                        productItem.innerHTML = `
                            <div class="added-product-image">
                                <img src="${productImage}" alt="${product.product_title}" class="h-100 object-fit-cover option-image w-100">
                                <a class="align-items-center bg-primary d-flex justify-content-center position-absolute remove-added-product rounded-circle text-white" href="#" data-remove-product="${productId}">
                                    <svg width="10" height="2" viewBox="0 0 10 2" aria-hidden="true" focusable="false">
                                        <use href="#icon-minus-thick"></use>
                                    </svg>
                                </a>
                                ${quantity > 1 ? `<a class="align-items-center bg-primary d-flex justify-content-center position-absolute remove-added-product-quantity rounded-circle text-white" href="javascript:void(0)">
                                    ${quantity}
                                </a>` : ''}
                                <span class="added-product-price-tag bg-primary-300 bottom-0 end-0 fs-12 position-absolute start-0 text-center fw-medium">
                                    +$${(product.price / 100).toFixed(2)}
                                </span>
                            </div>
                        `;

                        // Add click handler for remove button
                        const removeBtn = productItem.querySelector('[data-remove-product]');
                        if (removeBtn) {
                            removeBtn.addEventListener('click', (e) => {
                                e.preventDefault();
                                const productId = removeBtn.getAttribute('data-remove-product');
                                this.removeProductFromArray(currentStepNumber, stepIndex, freeProductLimit, productForm, productItem, currentStep, productId);
                            });
                        }

                        stepList.appendChild(productItem);
                    }
                }
            });
        }

        // Add product-added class to the product card
        const productCard = productForm.closest('[data-product-grid]');
        if (productCard) {
            productCard.classList.add('product-added');
            // Add NMC type to product card if it exists in properties
            const nmcType = this.productArray[stepIndex]?.find(p => p.nmc_type)?.nmc_type;

            if (nmcType) {
                // Convert 'nmc+' to 'nmc-plus' for valid attribute name
                const nmcTypeAttr = nmcType === 'nmc+' ? 'nmc-plus' : 'nmc';
                productCard.setAttribute(`data-${nmcTypeAttr}-type`, '');
            }
        }

        const allProductGrids = currentStep.querySelectorAll('[data-product-grid]');
        this.checkedAfterAdd(step);
        this.updateButtonText(freeProductLimit, allProductGrids, nmcCount, stepIndex);

         // Update next step button state based on nmcCount
         if (nextStepBtn) {
            if (nmcCount === parseInt(freeProductLimit)) {
                nextStepBtn.removeAttribute('disabled');
                nextStepBtn.classList.remove('disable-element');
                bottomBarParent.classList.add('choice-plus');
                // Show plus tag for current step
                const plusTag = currentStep.querySelector('[data-plus-tag]');
                if (plusTag) plusTag.classList.remove('d-none');
            } else {
                nextStepBtn.setAttribute('disabled', 'disabled');
                nextStepBtn.classList.add('disable-element');
                bottomBarParent.classList.remove('choice-plus');
                // Hide plus tag for current step
                const plusTag = currentStep.querySelector('[data-plus-tag]');
                if (plusTag) plusTag.classList.add('d-none');
            }
        }

        // Get the choice plus elements
        const choicePlusText = this.modal.querySelector('[data-choice-plus-text]');
        const choicePlusSubtitle = this.modal.querySelector('[data-choice-plus-subtitle]');
        const selectedTextElements = this.modal.querySelectorAll('[data-selected-text]');

        // Update visibility of choice plus and selected text elements
        if (nmcCount === parseInt(freeProductLimit) && nmcPlusCount === 0) {
            // When all NMC+ products are removed, show choice plus elements
            if (choicePlusText) choicePlusText.classList.remove('d-none');
            if (choicePlusSubtitle) choicePlusSubtitle.classList.remove('d-none');
            selectedTextElements.forEach(element => element.classList.add('d-none'));
        } else if(nmcCount === parseInt(freeProductLimit) && nmcPlusCount > 0) {
            // When all products are filled and at least one is NMC+, hide choice plus and show selected text
            if (choicePlusText) choicePlusText.classList.add('d-none');
            if (choicePlusSubtitle) choicePlusSubtitle.classList.add('d-none');
            selectedTextElements.forEach(element => {
                element.classList.remove('d-none');
                element.textContent = `${nmcCount} of ${freeProductLimit} selected`;
            });
        } else if (nmcCount <= parseInt(freeProductLimit)) {
            // When below limit, show selected text and hide choice plus
            selectedTextElements.forEach(element => {
                element.classList.remove('d-none');
                element.textContent = `${nmcCount} of ${freeProductLimit} selected`;
            });
            if (choicePlusText) choicePlusText.classList.add('d-none');
            if (choicePlusSubtitle) choicePlusSubtitle.classList.add('d-none');
        }
        
    }

    removeProductFromArray(currentStepNumber,stepIndex,freeProductLimit,productForm,productItem,currentStep,productId){
        const bottomBarWrapper = this.modal.querySelector('[data-bottom-bar-wrapper]');
        let stepList = bottomBarWrapper.querySelector(`[data-customized-step${currentStepNumber}]`);
        const allProductGrids = currentStep.querySelectorAll('[data-product-grid]');
        const nextStepBtn = this.modal.querySelector('[data-next-step]');
        const bottomBarParent = this.modal.querySelector('[data-bottom-bar-parent]');

        // Find or create the list for current step
        if (!stepList) {
            stepList = document.createElement('ul');
            stepList.className = `customized-step${currentStepNumber} added-product-list flex-wrap align-items-center d-flex list-unstyled m-0 p-0`;
            stepList.setAttribute(`data-customized-step${currentStepNumber}`, '');
            bottomBarWrapper.appendChild(stepList);
        }

        // Get the product image to identify the product
        const productImage = productItem.querySelector('img');
        // const productId = productItem.querySelector('[data-remove-product]')?.getAttribute('data-remove-product');
        
        // Remove from productArray
        if (this.productArray[stepIndex]) {
            // Find the index of the product to remove
            const productIndex = this.productArray[stepIndex].findIndex(item => 
                item.product_id === productId
            );
            
            if (productIndex !== -1) {
                // If quantity is more than 1, decrease it
                if (this.productArray[stepIndex][productIndex].quantity > 1) {
                    this.productArray[stepIndex][productIndex].quantity--;
                } else {
                    // If quantity is 1, remove the entire item
                    this.productArray[stepIndex].splice(productIndex, 1);
                }
            }
        }

        // Clear existing list items
        stepList.innerHTML = '';

        // Get filled items count from this.productArray using helper method
        const counts = this._getStepProductCounts(stepIndex);
        const nmcCount = counts.nmcCount;
        const nmcPlusCount = counts.nmcPlusCount;

        // First, add NMC products
        if (this.productArray[stepIndex]) {
            this.productArray[stepIndex].forEach(product => {
                if (product.nmc_type) {
                    if (product.nmc_type === 'nmc') {
                    const productItem = document.createElement('li');
                    productItem.className = 'added-product-item has-product';
                    productItem.setAttribute('data-nmc-type', '');

                    // Get image from properties
                    const productImage = product.addon_img;
                    let productId = product.product_id;
                    productItem.innerHTML = `
                        <div class="added-product-image">
                            <img src="${productImage}" alt="${product.product_title}" class="h-100 object-fit-cover option-image w-100">
                            <a class="align-items-center bg-primary d-flex justify-content-center position-absolute remove-added-product rounded-circle text-white" href="#" data-remove-product="${productId}">
                                <svg width="10" height="2" viewBox="0 0 10 2" aria-hidden="true" focusable="false">
                                    <use href="#icon-minus-thick"></use>
                                </svg>
                            </a>
                        </div>
                    `;

                    // Add click handler for remove button
                    const removeBtn = productItem.querySelector('[data-remove-product]');
                    if (removeBtn) {
                        removeBtn.addEventListener('click', (e) => {
                            e.preventDefault();
                            const productId = removeBtn.getAttribute('data-remove-product');
                            this.removeProductFromArray(currentStepNumber, stepIndex, freeProductLimit, productForm, productItem, currentStep, productId);
                        });
                    }

                    stepList.appendChild(productItem);
                }
                }
            });
        }

        // Add empty slots for remaining NMC products
        const remainingNmcSlots = parseInt(freeProductLimit) - nmcCount;
        for (let i = 0; i < remainingNmcSlots; i++) {
            const emptyItem = document.createElement('li');
            emptyItem.className = 'added-product-item';
            emptyItem.setAttribute('data-nmc-type', '');
            emptyItem.innerHTML = `
                <div class="added-product-image">
                    <img src="https://cdn.shopify.com/s/files/1/1336/5077/files/Products_Sticky_Bar_Account.svg?v=1746096804" 
                        alt="blank image" 
                        class="h-100 object-fit-contain w-100"
                        width="100"
                        height="100">
                </div>
            `;
            stepList.appendChild(emptyItem);
        }

        // Then, add NMC+ products
        if (this.productArray[stepIndex]) {
            this.productArray[stepIndex].forEach(product => {
                if (product.nmc_type) {
                    if (product.nmc_type === 'nmc+') {
                    const productItem = document.createElement('li');
                    productItem.className = 'added-product-item has-product';
                    productItem.setAttribute('data-nmc-plus-type', '');

                    // Get image from properties
                    const productImage = product.addon_img;
                    const productId = product.product_id;
                    const quantity = product.quantity || 1;
                    productItem.innerHTML = `
                        <div class="added-product-image">
                            <img src="${productImage}" alt="${product.product_title}" class="h-100 object-fit-cover option-image w-100">
                            <a class="align-items-center bg-primary d-flex justify-content-center position-absolute remove-added-product rounded-circle text-white" href="#" data-remove-product="${productId}">
                                <svg width="10" height="2" viewBox="0 0 10 2" aria-hidden="true" focusable="false">
                                    <use href="#icon-minus-thick"></use>
                                </svg>
                            </a>
                            ${quantity > 1 ? `<a class="align-items-center bg-primary d-flex justify-content-center position-absolute remove-added-product-quantity rounded-circle text-white" href="javascript:void(0)">
                                ${quantity}
                            </a>` : ''}
                            <span class="added-product-price-tag bg-primary-300 bottom-0 end-0 fs-12 position-absolute start-0 text-center fw-medium">
                                +$${(product.price / 100).toFixed(2)}
                            </span>
                        </div>
                    `;

                    // Add click handler for remove button
                    const removeBtn = productItem.querySelector('[data-remove-product]');
                    if (removeBtn) {
                        removeBtn.addEventListener('click', (e) => {
                            e.preventDefault();
                            const productId = removeBtn.getAttribute('data-remove-product');
                            this.removeProductFromArray(currentStepNumber, stepIndex, freeProductLimit, productForm, productItem, currentStep, productId);
                        });
                    }

                    stepList.appendChild(productItem);
                }
            }
            });
        }

        // Remove product-added class from the product card
        allProductGrids.forEach(productGrid => {
            const productCard = productGrid.getAttribute('data-product-grid');
            if (productCard === productId) {
                const matchingProducts = this.productArray[stepIndex]?.filter(p => 
                    p.product_id === productCard
                ) || [];
                
                // Sum up quantities of all matching products
                const totalQuantity = matchingProducts.reduce((sum, product) => sum + (product.quantity || 1), 0);
                if(totalQuantity === 0) productGrid.classList.remove('product-added');
            }
        });

        // Update next step button state based on nmcCount
        if (nextStepBtn) {
            if (nmcCount === parseInt(freeProductLimit)) {
                nextStepBtn.removeAttribute('disabled');
                nextStepBtn.classList.remove('disable-element');
                bottomBarParent.classList.add('choice-plus');
                // Show plus tag for current step
                const currentStep = this.modal.querySelector('[customization-data-step]:not(.d-none)');
                const plusTag = currentStep.querySelector('[data-plus-tag]');
                if (plusTag) plusTag.classList.remove('d-none');
            } else {    
                nextStepBtn.setAttribute('disabled', 'disabled');
                nextStepBtn.classList.add('disable-element');
                bottomBarParent.classList.remove('choice-plus');
                // Hide plus tag for current step
                const currentStep = this.modal.querySelector('[customization-data-step]:not(.d-none)');
                const plusTag = currentStep.querySelector('[data-plus-tag]');
                if (plusTag) plusTag.classList.add('d-none');
            }
        }
        

        // Get the choice plus elements
        const choicePlusText = this.modal.querySelector('[data-choice-plus-text]');
        const choicePlusSubtitle = this.modal.querySelector('[data-choice-plus-subtitle]');
        const selectedTextElements = this.modal.querySelectorAll('[data-selected-text]');  

        // console.log('NMC Count:', nmcCount, 'NMC+ Count:', nmcPlusCount, 'Free Product Limit:', freeProductLimit);
        this.updateButtonText(freeProductLimit, allProductGrids, nmcCount, stepIndex);

        // Update visibility of choice plus and selected text elements
        if (nmcCount === parseInt(freeProductLimit) && nmcPlusCount === 0) {
            // When all NMC+ products are removed, show choice plus elements
            if (choicePlusText) choicePlusText.classList.remove('d-none');
            if (choicePlusSubtitle) choicePlusSubtitle.classList.remove('d-none');
            selectedTextElements.forEach(element => element.classList.add('d-none'));
        }else if(nmcCount === parseInt(freeProductLimit) && nmcPlusCount > 0){
            if (choicePlusText) choicePlusText.classList.add('d-none');
            if (choicePlusSubtitle) choicePlusSubtitle.classList.add('d-none');
            selectedTextElements.forEach(element => element.classList.add('d-none'));
        }else if (nmcCount <= parseInt(freeProductLimit)) {
            // When below limit, show selected text
            selectedTextElements.forEach(element => {
                element.classList.remove('d-none');
                element.textContent = `${nmcCount} of ${freeProductLimit} selected`;
            });
            if (choicePlusText) choicePlusText.classList.add('d-none');
            if (choicePlusSubtitle) choicePlusSubtitle.classList.add('d-none');
        }

        // Log the current state after removing
        this._logNMCSelectionState('Product Removed', stepIndex, freeProductLimit);

        this.checkedAfterRemove();
    }

    checkedAfterRemove(){
        const currentStep = this.modal.querySelector('[customization-data-step]:not(.d-none)');
        const currentStepNumber = parseInt(currentStep.getAttribute('customization-data-step'));
        const stepIndex = currentStepNumber - 1;
        const freeProductLimit = currentStep.querySelector('[data-free-product-limit]')?.getAttribute('data-free-product-limit') || '0';

        // Use the new helper method to get accurate counts
        const counts = this._getStepProductCounts(stepIndex);

        // CORRECTED: After removing, only switch to NMC mode if there's now room for NMC products
        // This allows users to add NMC products again when slots open up
        if(counts.nmcCount < parseInt(freeProductLimit)){
            this.updateToNMCPlus('nmc');
            console.log('[NMC Selection] NMC slots available after removal, switching unadded products to NMC');
        }
        // If nmcCount >= limit, keep current mode (don't force NMC+)
        // because there might already be NMC+ products selected
    }

    updateButtonText(freeProductLimit,allProductGrids,filledItemsCount,stepIndex){
        // console.log('updateButtonText',freeProductLimit,filledItemsCount);

        // Get accurate counts using helper method
        const counts = this._getStepProductCounts(stepIndex);
        const isNMCLimitReached = counts.nmcCount >= parseInt(freeProductLimit);

        allProductGrids.forEach(productGrid => {
            const productForm = productGrid.querySelector('product-form');
            let isNMCPlus = false;
            let isNMC = false;

            if (productForm) {
                const variantRadios = productForm.querySelector('variant-radios');
                if (variantRadios && variantRadios.currentVariant) {
                    const option1 = variantRadios.currentVariant.option1?.toLowerCase() || '';
                    const option2 = variantRadios.currentVariant.option2?.toLowerCase() || '';
                    const option3 = variantRadios.currentVariant.option3?.toLowerCase() || '';
                    
                    isNMCPlus = option1.includes('nmc+') || option2.includes('nmc+') || option3.includes('nmc+');
                    isNMC = (option1.includes('nmc') || option2.includes('nmc') || option3.includes('nmc')) && 
                           !(option1.includes('nmc+') || option2.includes('nmc+') || option3.includes('nmc+'));
                }
            }

            const qtyContainer = productGrid.querySelector('[data-qty-container]');
            const removeButton = productGrid.querySelector('[data-remove-product-btn]');

            const productId = productGrid.getAttribute('data-product-grid');
            const matchingProducts = this.productArray[stepIndex]?.filter(p => 
                p.product_id === productId
            ) || [];
            
            // Sum up quantities of all matching products
            const totalQuantity = matchingProducts.reduce((sum, product) => sum + (product.quantity || 1), 0);
            
            if (productGrid.classList.contains('product-added')) {
                if(isNMCPlus){
                    // Show quantity container and enable its buttons
                    if (qtyContainer) {
                        qtyContainer.classList.remove('d-none');
                        const qtyValue = qtyContainer.querySelector('[data-qty-input]');
                        if (qtyValue) {
                            qtyValue.value = totalQuantity;
                        }
                        const qtyButtons = qtyContainer.querySelectorAll('[data-qty-btn]');
                        qtyButtons.forEach(btn => btn.classList.remove('disabled'));
                    }
                    // Hide remove button and add button
                    if (removeButton) {
                        removeButton.classList.remove('remove-btn');
                    }

                }else if(isNMC){
                    // Show quantity container and enable its buttons
                    if (qtyContainer) {
                        qtyContainer.classList.add('d-none');
                        const qtyButtons = qtyContainer.querySelectorAll('[data-qty-btn]');
                        qtyButtons.forEach(btn => btn.classList.remove('disabled'));
                    }
                    // Hide remove button and add button
                    if (removeButton) {
                        removeButton.classList.add('remove-btn');
                    }
                }
                
                
            }else{
                // Show quantity container and enable its buttons
                if (qtyContainer) {
                    qtyContainer.classList.add('d-none');
                    const qtyButtons = qtyContainer.querySelectorAll('[data-qty-btn]');
                    qtyButtons.forEach(btn => btn.classList.add('disabled'));
                }

                // Hide remove button and add button
                if (removeButton) {
                    removeButton.classList.add('remove-btn');
                }
            }

            // Update Add to Box button text for all product grids
            const addButton = productGrid.querySelector('[data-add-product-btn]');
            if (addButton) {
                const productForm = productGrid.querySelector('product-form');
                if (productForm) {
                    const variantRadios = productForm.querySelector('variant-radios');
                    if (variantRadios && variantRadios.currentVariant) {
                        const price = variantRadios.currentVariant.price / 100; // Convert cents to dollars
                        const addTextWrapper = addButton.querySelector('.add-text-wrapper');
                        const isProductAlreadyAdded = productGrid.classList.contains('product-added');

                        if (addTextWrapper) {
                            if (!variantRadios.currentVariant.available) {
                                addTextWrapper.textContent = 'Sold Out';
                                addButton.classList.add('btn-outline');
                                addButton.classList.remove('btn-dark');
                                addButton.setAttribute('disabled', 'disabled');
                            } else if (isNMC && isNMCLimitReached && !isProductAlreadyAdded) {
                                // CORRECTED: Only disable NMC products when NMC limit reached
                                // NMC+ products are never disabled
                                addTextWrapper.textContent = 'Switch to NMC+';
                                addButton.classList.add('btn-outline');
                                addButton.classList.remove('btn-dark');
                                addButton.setAttribute('disabled', 'disabled');
                            } else if (isNMCPlus || (isNMC && isNMCLimitReached)) {
                                // Show price for NMC+ products or when switching from NMC to NMC+
                                addTextWrapper.textContent = `Add to Box +$${price}`;
                                addButton.classList.remove('btn-outline');
                                addButton.classList.add('btn-dark');
                                addButton.removeAttribute('disabled');
                            } else {
                                // NMC products when under limit
                                addTextWrapper.textContent = `Add`;
                                addButton.classList.add('btn-outline');
                                addButton.classList.remove('btn-dark');
                                addButton.removeAttribute('disabled');
                            }
                        }
                    }
                }
            }
        });
    }

    switchToSubscriptionCollection(subscriptionType) {
        console.log('Switching to subscription collection:', subscriptionType);
        
        // Remove unnecessary subscription type elements completely
        const subscriptionTypeElements = document.querySelectorAll('[data-build-box-subscription-type]');
        console.log('Found subscription type elements:', subscriptionTypeElements.length);
        
        subscriptionTypeElements.forEach((element, index) => {
            const elementType = element.getAttribute('data-build-box-subscription-type');
            console.log(`Element ${index}: ${elementType}`);
            
            if (elementType === subscriptionType) {
                element.classList.remove('d-none');
                console.log(`Showing element: ${elementType}`);
            } else {
                // Completely remove the element from DOM instead of hiding
                element.remove();
                console.log(`Removed element: ${elementType}`);
            }
        });
        
        // Update collection handles for JavaScript functionality
        const productsLists = document.querySelectorAll('.customization-products-list');
        console.log('Found products lists:', productsLists.length);
        
        productsLists.forEach((productsList, index) => {
            let collectionHandle = '';
            
            // Get the appropriate collection handle based on subscription type
            switch (subscriptionType) {
                case "subscription_type_women":
                    collectionHandle = productsList.getAttribute('data-women-customization-collection-handle');
                    break;
                case "subscription_type_men":
                    collectionHandle = productsList.getAttribute('data-men-customization-collection-handle');
                    break;
                case "subscription_type_beauty":
                    collectionHandle = productsList.getAttribute('data-beauty-customization-collection-handle');
                    break;
                case "subscription_type_home":
                    collectionHandle = productsList.getAttribute('data-home-customization-collection-handle');
                    break;
                default:
                    console.log('Unknown subscription type:', subscriptionType);
                    return;
            }
            
            console.log(`Products list ${index} collection handle:`, collectionHandle);
            
            if (collectionHandle) {
                // Update the main collection handle attribute
                productsList.setAttribute('data-customization-collection-handle', collectionHandle);
                console.log(`Updated products list ${index} to use collection:`, collectionHandle);
            } else {
                console.log(`No collection handle found for ${subscriptionType} in products list ${index}`);
            }
        });
        
        // Handle progress steps and customization steps - hide/remove steps for groups without collections
        const progressSteps = document.querySelectorAll('[customization-data-step-inner]');
        const customizationSteps = document.querySelectorAll('[customization-data-step]');
        console.log('Found progress steps:', progressSteps.length);
        console.log('Found customization steps:', customizationSteps.length);
        
        // Process both progress steps and customization steps
        const allSteps = [...progressSteps, ...customizationSteps];
        
        allSteps.forEach((step, index) => {
            const stepNumber = parseInt(step.getAttribute('customization-data-step-inner') || step.getAttribute('customization-data-step'));
            console.log(`Processing step ${index}, step number: ${stepNumber}`);
            
            // Find the corresponding products list for this step
            const correspondingProductsList = productsLists[stepNumber - 1]; // stepNumber is 1-based, array is 0-based
            if (correspondingProductsList) {
                let collectionHandle = '';
                
                // Get the appropriate collection handle based on subscription type
                switch (subscriptionType) {
                    case "subscription_type_women":
                        collectionHandle = correspondingProductsList.getAttribute('data-women-customization-collection-handle');
                        break;
                    case "subscription_type_men":
                        collectionHandle = correspondingProductsList.getAttribute('data-men-customization-collection-handle');
                        break;
                    case "subscription_type_beauty":
                        collectionHandle = correspondingProductsList.getAttribute('data-beauty-customization-collection-handle');
                        break;
                    case "subscription_type_home":
                        collectionHandle = correspondingProductsList.getAttribute('data-home-customization-collection-handle');
                        break;
                }
                
                console.log(`Step ${stepNumber} collection handle:`, collectionHandle);
                
                if (collectionHandle && collectionHandle.trim() !== '') {
                    // Show the step - it has a collection assigned
                    step.classList.remove('d-none');
                    console.log(`Showing step ${stepNumber}`);
                } else {
                    // Completely remove the step from DOM - no collection assigned
                    step.remove();
                    console.log(`Removed step ${stepNumber} from DOM - no collection assigned`);
                }
            } else {
                console.log(`No corresponding products list found for step ${stepNumber}`);
            }
        });
    }
}

customElements.define('funnel-customization', FunnelCustomization);